// /home/labdoo/zitech-nexus/frontend/src/pages/admin/portfolio/AdminProjectForm.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { portfolioAPI } from '../../../services/api';
import './PortfolioAdmin.css';

// SVG Icons
const Icons = {
  Back: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  Save: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Cancel: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Plus: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5V19M5 12H19" />
    </svg>
  ),
  X: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Spinner: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pa-spinner-small">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Link: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Github: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.15 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.62.24 2.85.12 3.15.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z" />
    </svg>
  ),
};

interface ProjectFormData {
  title: string;
  category: string;
  description: string;
  client: string;
  year: string;
  technologies: string[];
  features: string[];
  cover_image: File | null;
  gallery_images: File[];
  live_demo_url: string;
  github_url: string;
  is_featured: boolean;
  is_published: boolean;
  order: number;
  meta_title: string;
  meta_description: string;
}

const AdminProjectForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [techInput, setTechInput] = useState('');
  const [featureInput, setFeatureInput] = useState('');
  
  const [formData, setFormData] = useState<ProjectFormData>({
    title: '',
    category: 'web_development',
    description: '',
    client: '',
    year: new Date().getFullYear().toString(),
    technologies: [],
    features: [],
    cover_image: null,
    gallery_images: [],
    live_demo_url: '',
    github_url: '',
    is_featured: false,
    is_published: true,
    order: 0,
    meta_title: '',
    meta_description: '',
  });

  const categories = [
    { value: 'web_development', label: 'Web Development' },
    { value: 'mobile_development', label: 'Mobile Development' },
    { value: 'software_integration', label: 'Software Integration' },
    { value: 'data_analytics', label: 'Data Analytics' },
    { value: 'cloud_services', label: 'Cloud Services' },
    { value: 'ai_ml', label: 'AI & Machine Learning' },
  ];

  useEffect(() => {
    if (id) {
      fetchProject();
    } else {
      setFetching(false);
    }
  }, [id]);

  const fetchProject = async () => {
    try {
      setFetching(true);
      setError('');
      const response = await portfolioAPI.adminGetProject(id!);
      
      const project = response.data;
      setFormData({
        title: project.title,
        category: project.category,
        description: project.description,
        client: project.client,
        year: project.year,
        technologies: project.technologies || [],
        features: project.features || [],
        cover_image: null,
        gallery_images: [],
        live_demo_url: project.live_demo_url || '',
        github_url: project.github_url || '',
        is_featured: project.is_featured,
        is_published: project.is_published,
        order: project.order || 0,
        meta_title: project.meta_title || '',
        meta_description: project.meta_description || '',
      });
    } catch (err: any) {
      console.error('Error fetching project:', err);
      setError('Failed to load project');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    if (error) setError('');
  };

  const handleAddTech = () => {
    if (techInput.trim() && !formData.technologies.includes(techInput.trim())) {
      setFormData(prev => ({
        ...prev,
        technologies: [...prev.technologies, techInput.trim()]
      }));
      setTechInput('');
    }
  };

  const handleRemoveTech = (tech: string) => {
    setFormData(prev => ({
      ...prev,
      technologies: prev.technologies.filter(t => t !== tech)
    }));
  };

  const handleAddFeature = () => {
    if (featureInput.trim() && !formData.features.includes(featureInput.trim())) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }));
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (feature: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter(f => f !== feature)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (id) {
        const submitData = {
          title: formData.title,
          category: formData.category,
          description: formData.description,
          client: formData.client,
          year: formData.year,
          technologies: formData.technologies,
          features: formData.features,
          live_demo_url: formData.live_demo_url || null,
          github_url: formData.github_url || null,
          is_featured: formData.is_featured,
          is_published: formData.is_published,
          order: formData.order,
          meta_title: formData.meta_title || '',
          meta_description: formData.meta_description || '',
        };

        await portfolioAPI.adminUpdateProject(id, submitData);
        alert('Project updated successfully!');
      } else {
        const submitData = new FormData();
        submitData.append('title', formData.title);
        submitData.append('category', formData.category);
        submitData.append('description', formData.description);
        submitData.append('client', formData.client);
        submitData.append('year', formData.year);
        submitData.append('technologies', JSON.stringify(formData.technologies));
        submitData.append('features', JSON.stringify(formData.features));
        submitData.append('live_demo_url', formData.live_demo_url || '');
        submitData.append('github_url', formData.github_url || '');
        submitData.append('is_featured', String(formData.is_featured));
        submitData.append('is_published', String(formData.is_published));
        submitData.append('order', String(formData.order));
        submitData.append('meta_title', formData.meta_title || '');
        submitData.append('meta_description', formData.meta_description || '');

        await portfolioAPI.adminCreateProject(submitData);
        alert('Project created successfully!');
      }

      navigate('/dashboard/admin/portfolio/projects');
    } catch (err: any) {
      console.error('Error saving project:', err);
      setError('Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="pa-loading-container">
        <div className="pa-spinner">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pa-spinner-svg">
            <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
        </div>
        <p className="pa-loading-text">Loading project...</p>
      </div>
    );
  }

  if (error && !formData.title) {
    return (
      <div className="pa-error-container">
        <div className="pa-error-icon">⚠️</div>
        <h2 className="pa-error-title">Error Loading Project</h2>
        <p className="pa-error-text">{error}</p>
        <button className="pa-btn-secondary" onClick={() => navigate('/dashboard/admin/portfolio/projects')}>
          <Icons.Back />
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="pa-form-container">
      {/* Header */}
      <div className="pa-form-header">
        <div className="pa-form-header-left">
          <button className="pa-back-btn" onClick={() => navigate('/dashboard/admin/portfolio/projects')}>
            <Icons.Back />
            Back to Projects
          </button>
          <h1 className="pa-form-title">{id ? 'Edit Project' : 'Create New Project'}</h1>
        </div>
        <button className="pa-btn-secondary" onClick={() => navigate('/dashboard/admin/portfolio/projects')}>
          <Icons.Cancel />
          Cancel
        </button>
      </div>

      {error && <div className="pa-error">{error}</div>}

      {/* Form */}
      <form onSubmit={handleSubmit} className="pa-form">
        <div className="pa-form-grid">
          {/* Left Column */}
          <div className="pa-form-left">
            <div className="pa-card">
              <h3 className="pa-card-title">Basic Information</h3>
              
              <div className="pa-form-group">
                <label className="pa-form-label">Title *</label>
                <input
                  type="text"
                  name="title"
                  className="pa-form-input"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="pa-form-group">
                <label className="pa-form-label">Category *</label>
                <select name="category" className="pa-form-select" value={formData.category} onChange={handleChange} required>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div className="pa-form-group">
                <label className="pa-form-label">Description *</label>
                <textarea
                  name="description"
                  className="pa-form-textarea"
                  value={formData.description}
                  onChange={handleChange}
                  rows={5}
                  required
                />
              </div>

              <div className="pa-form-row">
                <div className="pa-form-group">
                  <label className="pa-form-label">Client *</label>
                  <input
                    type="text"
                    name="client"
                    className="pa-form-input"
                    value={formData.client}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="pa-form-group">
                  <label className="pa-form-label">Year *</label>
                  <input
                    type="text"
                    name="year"
                    className="pa-form-input"
                    value={formData.year}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="pa-card">
              <h3 className="pa-card-title">
                <Icons.Link />
                Links & SEO
              </h3>
              
              <div className="pa-form-row">
                <div className="pa-form-group">
                  <label className="pa-form-label">Live Demo URL</label>
                  <input
                    type="url"
                    name="live_demo_url"
                    className="pa-form-input"
                    value={formData.live_demo_url}
                    onChange={handleChange}
                    placeholder="https://example.com"
                  />
                </div>
                <div className="pa-form-group">
                  <label className="pa-form-label">
                    <Icons.Github />
                    GitHub URL
                  </label>
                  <input
                    type="url"
                    name="github_url"
                    className="pa-form-input"
                    value={formData.github_url}
                    onChange={handleChange}
                    placeholder="https://github.com/..."
                  />
                </div>
              </div>

              <div className="pa-form-group">
                <label className="pa-form-label">Meta Title</label>
                <input
                  type="text"
                  name="meta_title"
                  className="pa-form-input"
                  value={formData.meta_title}
                  onChange={handleChange}
                />
              </div>

              <div className="pa-form-group">
                <label className="pa-form-label">Meta Description</label>
                <textarea
                  name="meta_description"
                  className="pa-form-textarea"
                  value={formData.meta_description}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="pa-form-right">
            <div className="pa-card">
              <h3 className="pa-card-title">Technologies</h3>
              <div className="pa-tag-group">
                <div className="pa-tag-input">
                  <input
                    type="text"
                    value={techInput}
                    onChange={(e) => setTechInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTech()}
                    placeholder="Type technology and press Enter"
                  />
                  <button type="button" onClick={handleAddTech}>
                    <Icons.Plus />
                  </button>
                </div>
                <div className="pa-tags">
                  {formData.technologies.map((tech, idx) => (
                    <span key={idx} className="pa-tag">
                      {tech}
                      <button type="button" onClick={() => handleRemoveTech(tech)}>
                        <Icons.X />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="pa-card">
              <h3 className="pa-card-title">Key Features</h3>
              <div className="pa-tag-group">
                <div className="pa-tag-input">
                  <input
                    type="text"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddFeature()}
                    placeholder="Type feature and press Enter"
                  />
                  <button type="button" onClick={handleAddFeature}>
                    <Icons.Plus />
                  </button>
                </div>
                <div className="pa-tags">
                  {formData.features.map((feature, idx) => (
                    <span key={idx} className="pa-tag">
                      {feature}
                      <button type="button" onClick={() => handleRemoveFeature(feature)}>
                        <Icons.X />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="pa-card">
              <h3 className="pa-card-title">Settings</h3>
              
              <div className="pa-form-group pa-form-checkbox">
                <label className="pa-checkbox-label">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleChange}
                  />
                  <span className="pa-checkbox-text">Featured Project</span>
                </label>
              </div>

              <div className="pa-form-group pa-form-checkbox">
                <label className="pa-checkbox-label">
                  <input
                    type="checkbox"
                    name="is_published"
                    checked={formData.is_published}
                    onChange={handleChange}
                  />
                  <span className="pa-checkbox-text">Publish</span>
                </label>
                <span className="pa-form-hint">Unpublished projects won't appear on the public website</span>
              </div>

              <div className="pa-form-group">
                <label className="pa-form-label">Display Order</label>
                <input
                  type="number"
                  name="order"
                  className="pa-form-input"
                  value={formData.order}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="pa-form-actions">
          <button 
            type="button" 
            className="pa-btn-secondary" 
            onClick={() => navigate('/dashboard/admin/portfolio/projects')}
          >
            Cancel
          </button>
          <button type="submit" className="pa-btn-primary" disabled={loading}>
            {loading ? (
              <>
                <Icons.Spinner />
                Saving...
              </>
            ) : (
              <>
                <Icons.Save />
                {id ? 'Update Project' : 'Create Project'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminProjectForm;